import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../../../backend/convex/_generated/api';
import type { Doc } from '../../../../../backend/convex/_generated/dataModel';
import { WorkspaceAdminLayout } from '../../components/WorkspaceAdminLayout';
import { useWorkspace } from '../../hooks/useWorkspace';
import { AlertModal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { Link } from 'react-router-dom';
import { generateSlug } from '../../utils/slug';

type WorkspaceWithOrg = Doc<"workspaces"> & { 
  role: string | null;
  organization?: Doc<"organizations"> | null;
};

export const WorkspaceSettings: React.FC = () => {
  const { workspace, isAdmin } = useWorkspace();
  const workspaceWithOrg = workspace as WorkspaceWithOrg | null;
  const resetJoinCode = useMutation(api.workspaces.resetJoinCode);
  const updateFeatures = useMutation(api.workspaces.updateFeatures);
  
  // Organization state
  const organizations = useQuery(api.organizations.listMyOrganizations);
  const createOrg = useMutation(api.organizations.create);
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", slug: "" });
  const [isSlugEdited, setIsSlugEdited] = useState(false);

  useEffect(() => {
    document.title = 'LibreRank - Settings';
  }, []);

  // Modal state
  const [alert, setAlert] = useState<{ isOpen: boolean; title: string; message: string; type: 'error' | 'success' }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success'
  });

  const [showConfirmReset, setShowConfirmReset] = useState(false);

  const handleResetJoinCode = async () => {
    if (workspace) {
      try {
        await resetJoinCode({ workspaceId: workspace._id });
        setAlert({
          isOpen: true,
          title: 'Code Reset',
          message: 'The workspace join code has been successfully updated.',
          type: 'success'
        });
      } catch (err) {
        setAlert({
          isOpen: true,
          title: 'Reset Failed',
          message: err instanceof Error ? err.message : 'An unknown error occurred',
          type: 'error'
        });
      } finally {
        setShowConfirmReset(false);
      }
    }
  };

  const handleFeatureToggle = async (feature: string, value: boolean) => {
    if (!workspace) return;
    
    try {
      const currentFeatures = workspace.features || {
        submissions: false,
        assessments: false,
        assignments: false,
      };

      await updateFeatures({
        workspaceId: workspace._id,
        features: {
          submissions: currentFeatures.submissions ?? false,
          assessments: currentFeatures.assessments ?? false,
          assignments: currentFeatures.assignments ?? false,
          [feature]: value,
        },
      });
    } catch (error) {
      setAlert({
        isOpen: true,
        title: 'Update Failed',
        message: 'Failed to update workspace features.',
        type: 'error'
      });
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createOrg({ name: newOrg.name, slug: newOrg.slug });
      setIsCreatingOrg(false);
      setNewOrg({ name: "", slug: "" });
      setIsSlugEdited(false);
      setAlert({
        isOpen: true,
        title: 'Success',
        message: 'Organization created successfully.',
        type: 'success'
      });
    } catch (error) {
      setAlert({
        isOpen: true,
        title: 'Error',
        message: error instanceof Error ? error.message : "Failed to create organization",
        type: 'error'
      });
    }
  };

  const handleNameChange = (name: string) => {
    setNewOrg(prev => ({
      ...prev,
      name,
      slug: isSlugEdited ? prev.slug : generateSlug(name)
    }));
  };

  const handleSlugChange = (slug: string) => {
    setIsSlugEdited(true);
    setNewOrg(prev => ({ ...prev, slug: slug.toLowerCase().replace(/\s+/g, '-') }));
  };

  if (!isAdmin) {
    return (
      <WorkspaceAdminLayout title="Workspace Settings">
        <div style={{ color: 'var(--destructive)' }}>
          Only workspace owners/admins can modify these settings.
        </div>
      </WorkspaceAdminLayout>
    );
  }

  return (
    <WorkspaceAdminLayout title="Workspace Settings">
      <AlertModal 
        isOpen={alert.isOpen} 
        onClose={() => setAlert({ ...alert, isOpen: false })} 
        title={alert.title} 
        message={alert.message} 
        type={alert.type} 
      />
      
      <ConfirmModal 
        isOpen={showConfirmReset}
        title="Reset Join Code"
        message="Are you sure you want to reset the join code? Existing codes will no longer work, and users with the old code will not be able to join."
        confirmText="Reset Code"
        onConfirm={handleResetJoinCode}
        onCancel={() => setShowConfirmReset(false)}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <section style={{ padding: '1.5rem', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--card)' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>General Information</h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--muted-foreground)', marginBottom: '0.25rem' }}>Workspace Name</label>
              <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>{workspace?.name}</div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--muted-foreground)', marginBottom: '0.25rem' }}>Workspace Slug</label>
              <div style={{ fontFamily: 'monospace', background: 'var(--muted)', padding: '0.25rem 0.5rem', borderRadius: '4px', display: 'inline-block' }}>
                {workspace?.slug}
              </div>
            </div>
          </div>
        </section>

        {/* Feature Flags Section */}
        <section style={{ padding: '1.5rem', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--card)' }}>
          <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Feature Management</h3>
          <p style={{ color: 'var(--muted-foreground)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            Enable or disable specific features for this workspace. Disabling features can help reduce costs or simplify the user experience.
          </p>
          
          <div style={{ display: 'grid', gap: '1rem' }}>
            {[
              { id: 'submissions', label: 'Code Submissions', desc: 'Allow users to submit code for grading.' },
              { id: 'assessments', label: 'Assessments', desc: 'Enable timed assessments and exams.' },
              { id: 'assignments', label: 'Assignments', desc: 'Allow assigning specific tasks to users.' },
            ].map((feature) => (
              <div key={feature.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', background: 'var(--muted)', borderRadius: '8px' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{feature.label}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted-foreground)' }}>{feature.desc}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    checked={workspace?.features?.[feature.id as keyof typeof workspace.features] ?? false}
                    onChange={(e) => handleFeatureToggle(feature.id, e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            ))}
          </div>
        </section>

        {/* Organization Section */}
        <section style={{ padding: '1.5rem', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--card)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Organization & Billing</h3>
          {!workspaceWithOrg?.organization && (
              <button 
                onClick={() => setIsCreatingOrg(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1.5 rounded-lg text-sm font-medium"
              >
                Create Organization
              </button>
            )}
          </div>

          {workspaceWithOrg?.organization ? (
            <div style={{ background: 'var(--muted)', padding: '1.25rem', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{workspaceWithOrg.organization.name}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--muted-foreground)' }}>/{workspaceWithOrg.organization.slug}</div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <Link 
                    to={`/org/${workspaceWithOrg.organization.slug}/admin/billing`}
                    className="bg-secondary hover:bg-accent text-foreground px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    Billing Settings
                  </Link>
                </div>
              </div>
              <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--muted-foreground)', marginBottom: 0 }}>
                This workspace is managed by {workspaceWithOrg.organization.name}. Subscription and billing are managed at the organization level.
              </p>
            </div>
          ) : (
            <div>
              <p style={{ color: 'var(--muted-foreground)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                This workspace is not currently linked to an organization. Organizations allow you to manage multiple workspaces, handle billing centrally, and upgrade to professional features.
              </p>
              
              {isCreatingOrg ? (
                <div style={{ background: 'var(--muted)', padding: '1.5rem', borderRadius: '8px' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>New Organization</h4>
                  <form onSubmit={handleCreateOrg} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>Name</label>
                        <input 
                          type="text" 
                          placeholder="Organization Name" 
                          required 
                          value={newOrg.name}
                          onChange={e => handleNameChange(e.target.value)}
                          style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--background)' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>Slug</label>
                        <input 
                          type="text" 
                          placeholder="org-slug" 
                          required 
                          value={newOrg.slug}
                          onChange={e => handleSlugChange(e.target.value)}
                          style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--background)' }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                      <button type="button" onClick={() => { setIsCreatingOrg(false); setIsSlugEdited(false); }} className="text-sm font-medium px-3 py-2">Cancel</button>
                      <button type="submit" className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium">Create & Link</button>
                    </div>
                  </form>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                  {organizations?.map((org: Doc<"organizations">) => (
                    <div key={org._id} style={{ border: '1px solid var(--border)', padding: '1rem', borderRadius: '8px' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: '0.5rem' }}>{org.name}</div>
                      <button 
                        disabled
                        className="w-full text-xs bg-muted text-muted-foreground py-1.5 rounded opacity-50 cursor-not-allowed"
                      >
                        Link (Coming Soon)
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section style={{ padding: '1.5rem', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--card)' }}>
          <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Access & Enrollment</h3>
          <p style={{ color: 'var(--muted-foreground)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            Manage how users can join your workspace.
          </p>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'var(--muted)', padding: '1rem', borderRadius: '8px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--muted-foreground)', marginBottom: '0.25rem' }}>Join Code</label>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: '0.1em', fontFamily: 'monospace' }}>
                {workspace?.joinCode || 'None'}
              </div>
            </div>
            <button 
              onClick={() => setShowConfirmReset(true)}
              style={{
                padding: '0.5rem 1rem',
                background: 'var(--secondary)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Reset Code
            </button>
          </div>
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--muted-foreground)' }}>
            Users can enter this code to automatically join the workspace as a Member.
          </p>
        </section>

        <section style={{ padding: '1.5rem', border: '1px solid var(--destructive-muted)', borderRadius: '8px', background: 'var(--card)' }}>
          <h3 style={{ marginTop: 0, marginBottom: '0.5rem', color: 'var(--destructive)' }}>Danger Zone</h3>
          <p style={{ color: 'var(--muted-foreground)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            Irreversible actions for this workspace.
          </p>
          <button 
            disabled
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              color: 'var(--destructive)',
              border: '1px solid var(--destructive)',
              borderRadius: '4px',
              cursor: 'not-allowed',
              opacity: 0.5,
              fontSize: '0.9rem'
            }}
          >
            Archive Workspace
          </button>
        </section>
      </div>
    </WorkspaceAdminLayout>
  );
};
